<template>
    <Modal size="md" allow-overflow @close="$emit('close')">
        <template #header>
            <div class="text-gray-600">Export to Lettr</div>
        </template>

        <template #body>
            <div class="overflow-visible p-6">
                <form>
                    <SelectInput
                        v-model="state.purpose"
                        placeholder="Choose purpose"
                        :data="purposes"
                        label="Purpose"
                        required
                        :error="getError('purpose')"
                        :disabled="loading"
                        @blurred="v$.purpose.$touch()"
                    />

                    <SelectInput
                        v-model="folderId"
                        class="mt-6"
                        :placeholder="folderPlaceholder"
                        :data="folders"
                        label="Folder"
                        :error="foldersError"
                        :disabled="loading || foldersLoading || folders.length === 0"
                    />

                    <p class="mt-2 text-sm text-gray-500">
                        Lettr keeps campaign and transactional templates apart,
                        each with its own folders.
                    </p>
                </form>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-between border-t border-gray-200 p-6">
                <AtomicButton color="white" size="md" @click="$emit('close')">
                    Cancel
                </AtomicButton>

                <AtomicButton
                    size="md"
                    color="yellow"
                    :disabled="loading || foldersLoading || v$.$invalid"
                    @click="exportToLettr"
                >
                    {{ preparing ? 'Lettr is preparing it…' : 'Export' }}
                </AtomicButton>
            </div>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import { PropType, computed, reactive, ref, toRef } from 'vue';
import { IIntegration, ILettr } from '@/types/Integrations/IIntegration';
import axios from 'axios';
import { route } from 'ziggy-js';
import { create } from '@composables/useNotifications';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import ISelectable from '@/types/Atomic/ISelectable';
import useLettrFolders from '../composables/useLettrFolders';

const emit = defineEmits(['close']);

const props = defineProps({
    integration: {
        required: true,
        type: Object as PropType<IIntegration<ILettr>>,
    },
    templateId: {
        required: true,
        type: Number,
    },
    templateName: {
        required: true,
        type: String,
    },
});

const state = reactive({
    purpose: 'campaign',
});

const rules = {
    purpose: {
        required,
    },
};

const v$ = useVuelidate(rules, state);

const loading = ref(false);
const preparing = ref(false);

const purposes = ref<ISelectable[]>([
    {
        title: 'Campaign',
        value: 'campaign',
    },
    {
        title: 'Transactional',
        value: 'transactional',
    },
]);

const {
    folders,
    folderId,
    loading: foldersLoading,
    error: foldersError,
    load: loadFolders,
} = useLettrFolders(
    toRef(() => props.integration.id),
    toRef(state, 'purpose')
);

loadFolders();

const folderPlaceholder = computed(() => {
    if (foldersLoading.value) {
        return 'Loading folders…';
    }

    return folders.value.length === 0
        ? 'No folders in Lettr'
        : 'Choose folder';
});

const getError = (field: string) => {
    return v$.value[field]?.$errors[0]?.$message || '';
};

/**
 * Lettr answers a create before it has migrated the images or rendered the
 * HTML. Reporting from that answer alone called the export done while the
 * template still could not be sent, so it is asked again until it settles.
 */
const SECONDS_BEFORE_FIRST_CHECK = 3;
const SECONDS_BETWEEN_CHECKS = 3;
const MAX_CHECKS = 40;

const wait = (seconds: number) =>
    new Promise((resolve) => setTimeout(resolve, seconds * 1000));

const waitForPreparation = async (
    slug: string,
    lettrFolderId: number
): Promise<'ready' | 'failed' | 'pending'> => {
    await wait(SECONDS_BEFORE_FIRST_CHECK);

    for (let check = 0; check < MAX_CHECKS; check++) {
        try {
            const response = await axios.get(
                route('integrations.lettr.template-status.show', {
                    integration: props.integration.id,
                }),
                {
                    params: {
                        slug,
                        folder_id: lettrFolderId,
                        purpose: state.purpose,
                    },
                }
            );

            if (response.data.status !== 'pending') {
                return response.data.status;
            }
        } catch (e) {
            // Not being able to ask says nothing about the export, which has
            // already happened; stop asking rather than call it a failure.
            return 'pending';
        }

        await wait(SECONDS_BETWEEN_CHECKS);
    }

    return 'pending';
};

const exportToLettr = async () => {
    loading.value = true;

    try {
        const response = await axios.post(
            route('templates.exports.lettr', {
                template: props.templateId,
            }),
            {
                integration_id: props.integration.id,
                purpose: state.purpose,
                folder_id: folderId.value === '' ? null : folderId.value,
            }
        );

        if (!response.data.success) {
            create({
                title: 'Unsuccessful export',
                text: 'Template has not been exported to Lettr.',
                type: 'error',
            });

            return;
        }

        preparing.value = true;

        const status = await waitForPreparation(
            response.data.slug,
            response.data.folder_id
        );

        if (status === 'failed') {
            create({
                title: 'Lettr could not prepare the template',
                text: 'It reached Lettr but rendering it there failed, so it cannot be sent yet.',
                type: 'error',
            });
            emit('close');

            return;
        }

        create({
            title: 'Successful export',
            text:
                status === 'ready'
                    ? 'Template has been successfully exported to Lettr.'
                    : 'Template is in Lettr, which is still preparing it.',
            type: status === 'ready' ? 'success' : 'info',
        });
        emit('close');
    } catch (e) {
        create({
            title: 'Unsuccessful export',
            text:
                axios.isAxiosError(e) && e.response?.data?.message
                    ? e.response.data.message
                    : 'Template has not been exported to Lettr.',
            type: 'error',
        });
    } finally {
        loading.value = false;
        preparing.value = false;
    }
};
</script>
